<template>
    <section class="flex h-[90vh] flex-col">
        <header class="modal-card-head is-radiusless">
            <p class="modal-card-title">Document Form Builder</p>
        </header>

        <section class="modal-card-body !p-0">
            <DocusealBuilder
                class="h-full w-full"
                :token="token"
                host="cdn.docuseal.eu"
                :field-types="['text', 'signature', 'date']"
                :with-send-button="false"
                :with-sign-yourself-button="false"
                :autosave="true"
                :with-upload-button="false"
                :with-add-page-button="false"
                custom-css=".document-control-button{display: none}"
                @save="handleSave"
                @load="handleLoad"
            />
        </section>

        <footer class="modal-card-foot">
            <div class="flex w-full justify-end gap-2">
                <NButton size="small" @click="handleClose"> Close </NButton>
                <NButton size="small" type="primary" @click="handleConfirm"> Confirm </NButton>
            </div>
        </footer>
    </section>
</template>

<script setup lang="ts">
import { MessageType, showMessage } from '@/shared';
import { DocusealBuilder } from '@docuseal/vue';
import { NButton } from 'naive-ui';
import { ref } from 'vue';

type DocusealTemplateResult = {
    id: number;
    submitters: { name: string; uuid: string }[];
    fields: {
        name: string;
        required: boolean;
        type: 'text' | 'signature' | 'date';
        submitter_uuid: string;
    }[];
};

interface Props {
    token: string;
}

interface Emits {
    (e: 'confirm', data: DocusealTemplateResult): void;
    (e: 'close'): void;
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const data = ref<DocusealTemplateResult | null>(null);

const handleClose = () => {
    emit('close');
};

const handleConfirm = () => {
    if (!data.value) {
        showMessage('Signature data not set', MessageType.Warning);
        return;
    }

    const submitters = data.value?.submitters;
    if (!submitters?.length) {
        showMessage('No signature parties found', MessageType.Warning);
        return;
    }

    for (const submitter of submitters) {
        const signatureField = data.value?.fields?.find(field => field?.submitter_uuid === submitter?.uuid && field?.type === 'signature');
        if (!signatureField) {
            showMessage(`Signature field not found for ${submitter.name}`, MessageType.Warning);
            return;
        }
    }

    emit('confirm', data.value);
};

const handleSave = (templateSaveResult: DocusealTemplateResult) => {
    data.value = templateSaveResult;
};

const handleLoad = (templateSaveResult: DocusealTemplateResult) => {
    data.value = templateSaveResult;
};
</script>
